import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu,  Cpu, Zap, Globe, ArrowRight } from 'lucide-react';

const GeorgiaTech: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Georgia Tech";

    return (
        <div className="min-h-screen bg-white font-sans text-[#545454] selection:bg-[#B3A369] selection:text-[#003057]">
            {/* Top Bar - Tech Gold */}
            <div className="bg-[#B3A369] text-[#003057] py-2 px-6 flex justify-end gap-6 text-xs font-bold uppercase tracking-wide">
                <a href="#" className="hover:underline">Offices</a>
                <a href="#" className="hover:underline">Directory</a>
                <a href="#" className="hover:underline">Campus Map</a>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-200 sticky top-0 z-50">
                <div className="max-w-[1200px] mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-3">
                         <img src="https://brand.gatech.edu/sites/default/files/inline-images/GT-seal.png" alt="Seal" className="h-10 w-10 opacity-80" />
                        <div className="flex flex-col">
                            <span className="font-serif text-2xl font-bold text-[#003057] leading-none">{schoolName}</span>
                        </div>
                    </div>

                    <div className="hidden lg:flex gap-8 font-bold text-[#003057] text-sm uppercase tracking-wide">
                        <a href="#" className="hover:text-[#B3A369] transition-colors">Admissions</a>
                        <a href="#" className="hover:text-[#B3A369] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#B3A369] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#B3A369] transition-colors">Campus Life</a>
                        <a href="#" className="hover:text-[#B3A369] transition-colors">News</a>
                    </div>
                    
                    <div className="flex gap-4 items-center">
                         <Search className="cursor-pointer text-[#003057] hover:text-[#B3A369]" size={22} />
                         <Menu className="cursor-pointer lg:hidden text-[#003057] hover:text-[#B3A369]" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Tech Green / Midtown */}
            <header className="relative h-[650px] bg-[#003057]">
                 <div className="absolute inset-0 opacity-40">
                      <div className="absolute inset-0 bg-[#003057]"></div>
                      {/* Abstract Tech Patterns could go here */}
                 </div>
                 <img 
                    src="https://images.unsplash.com/photo-1590432029352-2a326c924d57?q=80&w=2070&auto=format&fit=crop" 
                    alt="Clough Commons" 
                    className="absolute inset-0 w-full h-full object-cover mix-blend-overlay"
                />

                <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full px-8 md:px-12">
                     <div className="max-w-3xl mx-auto text-center text-white">
                         <h1 className="text-5xl md:text-7xl font-bold mb-8 uppercase tracking-tight">
                             Creating the Next
                         </h1>
                         <p className="text-xl md:text-2xl mb-12 font-light text-gray-200 max-w-2xl mx-auto">
                             We are a top-ranked public college and one of the leading research universities in the USA.
                         </p>
                         <div className="flex flex-col md:flex-row gap-4 justify-center">
                             <Button className="bg-[#B3A369] hover:bg-[#a09055] text-[#003057] font-bold uppercase tracking-widest px-10 h-14 rounded-none">
                                 Apply to {schoolName.split(' ')[0]}
                             </Button>
                             <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#003057] font-bold uppercase tracking-widest px-10 h-14 rounded-none bg-transparent">
                                 Visit Campus
                             </Button>
                         </div>
                     </div>
                </div>
            </header>

            {/* Impact Areas - Hexagons implied */}
            <section className="py-24 px-6 md:px-12 bg-white">
                <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                     <div className="p-8 border border-gray-200 hover:border-[#B3A369] transition-colors group">
                         <Cpu size={48} className="text-[#003057] mx-auto mb-6 group-hover:text-[#B3A369] transition-colors" />
                         <h3 className="text-2xl font-bold text-[#003057] mb-4">Technology</h3>
                         <p className="text-gray-600">Developing breakthrough technologies to improve the human condition.</p>
                     </div>
                      <div className="p-8 border border-gray-200 hover:border-[#B3A369] transition-colors group">
                         <Zap size={48} className="text-[#003057] mx-auto mb-6 group-hover:text-[#B3A369] transition-colors" />
                         <h3 className="text-2xl font-bold text-[#003057] mb-4">Innovation</h3>
                         <p className="text-gray-600">Fostering an ecosystem of entrepreneurship and startups.</p>
                     </div>
                      <div className="p-8 border border-gray-200 hover:border-[#B3A369] transition-colors group">
                         <Globe size={48} className="text-[#003057] mx-auto mb-6 group-hover:text-[#B3A369] transition-colors" />
                         <h3 className="text-2xl font-bold text-[#003057] mb-4">Global</h3>
                         <p className="text-gray-600">With campuses in France and China, we are truly global.</p>
                     </div>
                </div>
            </section>

             {/* Whistle Blog / News */}
             <section className="bg-[#f2f2f2] py-24 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto">
                     <div className="flex justify-between items-center mb-12">
                         <h2 className="text-3xl font-bold text-[#003057]">Institute News</h2>
                         <a href="#" className="text-[#003057] font-bold uppercase text-xs hover:text-[#B3A369]">View All</a>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                         {[
                             "New AI hub launches in Midtown Atlanta",
                             "Aerospace engineering students design next-gen drones",
                             "Scheller College of Business ranked top 10 for ROI",
                             "Yellow Jackets secure victory in season opener"
                         ].map((title, i) => (
                             <div key={i} className="bg-white p-6 shadow-sm border-b-4 border-transparent hover:border-[#B3A369] transition-all cursor-pointer group h-full flex flex-col justify-between">
                                 <h3 className="text-lg font-bold text-[#003057] mb-4 group-hover:text-[#003057]">{title}</h3>
                                 <div className="flex items-center gap-2 text-[#B3A369] font-bold text-xs uppercase">
                                     Read <ArrowRight size={12}/>
                                 </div>
                             </div>
                         ))}
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#262626] text-gray-400 py-16 px-6 md:px-12">
                 <div className="max-w-[1200px] mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 font-sans text-sm">
                     <div className="col-span-1 md:col-span-2">
                         <img src="https://brand.gatech.edu/sites/default/files/inline-images/GT-logo-gold.png" alt="Georgia Tech" className="h-12 mb-6 opacity-80" />
                         <span className="font-bold text-white text-lg block mb-2">{schoolName}</span>
                         <p className="mb-6">
                             North Avenue, Atlanta, GA 30332 <br/>
                             404.894.2000
                         </p>
                     </div>

                     <div className="grid grid-cols-2 gap-12 font-bold uppercase text-xs tracking-wide">
                          <ul className="space-y-3 hover:[&_a]:text-white">
                             <li><a href="#">Emergency</a></li>
                             <li><a href="#">Legal & Privacy</a></li>
                             <li><a href="#">Accessibility</a></li>
                             <li><a href="#">Accountability</a></li>
                         </ul>
                          <ul className="space-y-3 hover:[&_a]:text-white">
                             <li><a href="#">Employment</a></li>
                             <li><a href="#">Contact Us</a></li>
                             <li><a href="#">Offices & Departments</a></li>
                         </ul>
                     </div>
                 </div>
                 <div className="max-w-[1200px] mx-auto mt-16 text-center text-xs text-gray-600 border-t border-gray-800 pt-8">
                     © 2026 {schoolName}
                 </div>
             </footer>
        </div>
    );
};

export default GeorgiaTech;
